import { Vec2 } from '../../../consts/types';

interface SnowProps {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  parentElement: HTMLElement;
  onResize: () => void;
}

const Snow = ({ canvas, ctx, parentElement, onResize }: SnowProps) => {
  canvas.id = 'sfxCanvas';
  ctx.fillStyle = '#ffffff';

  interface Flake {
    x: number;
    y: number;
    speed: number;
    dir: number;
    rad: number;
  }

  const snow: Flake[] = [];
  const wind = { x: 0, y: 0 };
  const gravity = { x: 0, y: 1 };
  const threshold = 500;
  let time = 0;
  let pointerPos: Vec2 | undefined;

  function newFlake() {
    const y = Math.round(Math.random() * canvas.height) * -1;
    const x = Math.round(Math.random() * canvas.width * 2) - canvas.width / 2;
    const speed = Math.round(Math.random() * 3 + 0.3);
    const dir = Math.random() * 180;
    const rad = Math.random() * 5;
    snow.push({ x: x, y: y, speed: speed, dir: dir, rad: rad });
  }

  function draw() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    snow.forEach(function (flake) {
      ctx.beginPath();
      ctx.arc(flake.x, flake.y, flake.rad, 0, 2 * Math.PI, false);
      ctx.fill();
    });
  }

  function setPointerPos(e: TouchEvent | MouseEvent) {
    if (typeof (e as TouchEvent).touches !== 'undefined') {
      pointerPos = {
        x: (e as TouchEvent).touches[0].pageX - parentElement.offsetLeft,
        y: (e as TouchEvent).touches[0].pageY - parentElement.offsetTop
      };
    } else
      pointerPos = {
        x: (e as MouseEvent).pageX - parentElement.offsetLeft,
        y: (e as MouseEvent).pageY - parentElement.offsetTop
      };
  }

  function unsetPointerPos() {
    pointerPos = undefined;
  }

  function distance(vecA: Vec2, vecB: Vec2) {
    return Math.abs(Math.sqrt(Math.pow(vecB.x - vecA.x, 2) + Math.pow(vecB.y - vecA.y, 2)));
  }

  function normalize(pVec: Vec2) {
    const magnitude = Math.abs(Math.sqrt(Math.pow(pVec.x, 2) + Math.pow(pVec.y, 2)));
    const normVec = { x: pVec.x / magnitude, y: pVec.y / magnitude };
    return normVec;
  }

  function tick() {
    if (snow.length < threshold) {
      newFlake();
      //console.log(snow.length);
    }

    snow.forEach(function (flake, index) {
      let turbulence: Vec2 = { x: 0, y: 0 };
      if (pointerPos) {
        const dist = distance(pointerPos, flake);
        //console.log(dist);
        if (dist < 75) {
          turbulence = normalize({ x: flake.x - pointerPos.x, y: flake.y - pointerPos.y });
          turbulence.x *= (75 - dist) * 0.1;
          turbulence.y *= (75 - dist) * 0.1;
          if ('ontouchstart' in document.documentElement) {
            turbulence.x *= 10;
            turbulence.y *= 10;
          }
        }
      }
      flake.x += gravity.x + wind.x + turbulence.x + flake.speed;
      flake.y += gravity.y + wind.y + turbulence.y + flake.speed;
      if (flake.x > canvas.width * 2 || flake.y > canvas.height || flake.x < -canvas.width) {
        snow.splice(index, 1);
      }
    });
    draw();

    wind.x = 2 * Math.sin((time * Math.PI) / 180);
    time += 1;

    requestAnimationFrame(tick);
  }

  tick();

  parentElement.addEventListener('resize', function () {
    onResize();
    canvas.width = parentElement.offsetWidth;
    canvas.height = parentElement.offsetHeight;
    ctx.fillStyle = '#ffffff';
  });

  parentElement.addEventListener('mousemove', setPointerPos);
  parentElement.addEventListener('mouseleave', unsetPointerPos);

  parentElement.addEventListener('touchmove', setPointerPos);
  parentElement.addEventListener('touchend', unsetPointerPos);
};

export default Snow;
